import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { api } from '../../lib/api';

interface Props {
  collectionKey: string;
  fieldKey: string;
  // Contexto del elemento que se está editando (da pistas al modelo).
  itemContext?: Record<string, unknown>;
  // Recibe el valor generado: { es, en } si el campo es bilingüe, o escalar.
  onGenerated: (value: unknown) => void;
}

// Botón ✨ por campo: abre un popover con una instrucción opcional y genera el
// contenido vía /api/ai/field. Escribe ambos idiomas de una vez (decisión de producto).
export default function AiAssist({ collectionKey, fieldKey, itemContext, onGenerated }: Props) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function generate() {
    setLoading(true);
    setError('');
    try {
      const { value } = await api.ai.field(collectionKey, fieldKey, {
        instruction: instruction.trim() || undefined,
        itemContext,
      });
      onGenerated(value);
      setOpen(false);
      setInstruction('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={() => setOpen((o) => !o)}
        whileTap={{ scale: 0.9 }}
        animate={loading ? { scale: [1, 1.15, 1] } : { scale: 1 }}
        transition={loading ? { repeat: Infinity, duration: 0.9 } : { duration: 0.15 }}
        title="Generar con IA"
        className="flex items-center gap-1 text-xs text-ui-accent hover:text-ui-accent-hover transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" />
        {loading ? 'Generando…' : 'IA'}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-6 z-20 w-64 p-3 rounded-lg bg-ui-surface-2 border border-ui-border shadow-xl"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ui-text">Generar con IA</span>
              <button type="button" onClick={() => setOpen(false)} className="text-ui-text-subtle hover:text-ui-text">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={2}
              placeholder="Instrucción opcional (ej. tono cálido, menciona la ubicación)"
              className="w-full bg-ui-bg border border-ui-border rounded-md px-2 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent resize-none"
            />
            {error && <p className="mt-1.5 text-sm text-ui-danger">{error}</p>}
            <motion.button
              type="button"
              onClick={generate}
              disabled={loading}
              whileTap={{ scale: 0.97 }}
              className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-ui-accent text-ui-text-inverse text-xs font-medium rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {loading ? 'Generando…' : 'Generar'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
